/**
 * Map View Context
 *
 * Provides shared state for 2D/3D map view synchronization.
 * Manages camera position, view mode, and transitions between views.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { trackViewModeChange, trackViewIn3D } from '../utils/analytics';

// Camera state representing position and orientation
export interface CameraState {
  latitude: number;
  longitude: number;
  altitude: number; // meters above ground
  heading: number; // degrees from north (0-360)
  pitch: number; // degrees from horizon (-90 = looking down, 0 = horizon)
}

// View mode - 2D (Leaflet) or 3D (Cesium)
export type ViewMode = '2d' | '3d';

// Options for flying to a location
export interface FlyToOptions {
  altitude?: number;
  heading?: number;
  pitch?: number;
  duration?: number; // seconds
}

// Focused fire for 3D view
export interface FocusedFire {
  id: string;
  lat: number;
  lng: number;
  title: string;
  radiusKm: number; // Show fires within this radius
}

// Context value type
interface MapViewContextType {
  // Current view mode
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode, source?: string) => void;

  // Camera state
  camera: CameraState;
  updateCamera: (updates: Partial<CameraState>) => void;

  // Navigation
  flyTo: (lat: number, lng: number, options?: FlyToOptions) => void;

  // Pending fly-to request (consumed by map components)
  pendingFlyTo: { lat: number; lng: number; options?: FlyToOptions } | null;
  clearPendingFlyTo: () => void;

  // 3D-specific settings
  terrainExaggeration: number;
  setTerrainExaggeration: (value: number) => void;

  // Performance settings
  is3DEnabled: boolean; // Whether 3D mode is available (can be disabled for performance)
  setIs3DEnabled: (enabled: boolean) => void;

  // Focused fire mode - 3D view centers on a specific fire
  focusedFire: FocusedFire | null;
  setFocusedFire: (fire: FocusedFire | null) => void;
  enterFocused3DMode: (fire: { id: string; lat: number; lng: number; title: string }) => void;
  exitFocused3DMode: () => void;
}

// Default camera position - centered on Australia
const DEFAULT_CAMERA: CameraState = {
  latitude: -25.2744,
  longitude: 133.7751,
  altitude: 5000000, // 5000km - continent view
  heading: 0,
  pitch: -90, // Looking straight down for 2D-like view
};

const MapViewContext = createContext<MapViewContextType | null>(null);

interface MapViewProviderProps {
  children: ReactNode;
  initialViewMode?: ViewMode;
  initialCamera?: Partial<CameraState>;
}

export function MapViewProvider({
  children,
  initialViewMode = '2d',
  initialCamera,
}: MapViewProviderProps) {
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
  const [camera, setCamera] = useState<CameraState>({
    ...DEFAULT_CAMERA,
    ...initialCamera,
  });
  const [pendingFlyTo, setPendingFlyTo] = useState<{
    lat: number;
    lng: number;
    options?: FlyToOptions;
  } | null>(null);
  const [terrainExaggeration, setTerrainExaggeration] = useState(1);
  const [is3DEnabled, setIs3DEnabled] = useState(true);
  const [focusedFire, setFocusedFire] = useState<FocusedFire | null>(null);
  const cameraRef = useRef(camera);
  cameraRef.current = camera;

  // Update camera state
  const updateCamera = useCallback((updates: Partial<CameraState>) => {
    setCamera((prev) => ({ ...prev, ...updates }));
  }, []);

  // Request fly-to animation
  const flyTo = useCallback(
    (lat: number, lng: number, options?: FlyToOptions) => {
      setPendingFlyTo({ lat, lng, options });

      // Also update camera state for immediate reflection
      updateCamera({
        latitude: lat,
        longitude: lng,
        ...(options?.altitude && { altitude: options.altitude }),
        ...(options?.heading !== undefined && { heading: options.heading }),
        ...(options?.pitch !== undefined && { pitch: options.pitch }),
      });
    },
    [updateCamera]
  );

  // Clear pending fly-to after map component consumes it
  const clearPendingFlyTo = useCallback(() => {
    setPendingFlyTo(null);
  }, []);

  // Handle view mode change with camera adjustment
  const handleSetViewMode = useCallback(
    (mode: ViewMode, source?: string) => {
      // Track view mode change
      trackViewModeChange(mode, source || 'toggle_button');

      setViewMode((prevMode) => {
        // When switching to 3D, adjust pitch for better perspective
        if (mode === '3d' && prevMode === '2d') {
          updateCamera({
            pitch: -45, // Tilted view in 3D
            altitude: Math.max(cameraRef.current.altitude, 10000), // Ensure minimum altitude for 3D
          });
        }
        // When switching to 2D, reset to top-down view
        else if (mode === '2d' && prevMode === '3d') {
          updateCamera({
            pitch: -90, // Top-down in 2D
            heading: 0, // North up
          });
          // Clear focused fire when returning to 2D
          setFocusedFire(null);
        }
        return mode;
      });
    },
    [updateCamera]
  );

  // Enter focused 3D mode for a specific fire
  const enterFocused3DMode = useCallback(
    (fire: { id: string; lat: number; lng: number; title: string }) => {
      // Track this interaction
      trackViewIn3D(fire.id, fire.title);
      trackViewModeChange('3d', 'fire_detail_panel');

      const focused: FocusedFire = {
        ...fire,
        radiusKm: 50, // Default 50km radius
      };
      setFocusedFire(focused);

      // Switch to 3D mode
      setViewMode('3d');

      // Fly to the fire location with a good viewing angle
      // Note: Map3D will override this with wind-aware positioning
      flyTo(fire.lat, fire.lng, {
        altitude: 12000, // 12km altitude for immersive view
        pitch: -50, // Steeper angle to see fire clearly
        duration: 1.5,
      });
    },
    [flyTo]
  );

  // Exit focused 3D mode and return to 2D
  const exitFocused3DMode = useCallback(() => {
    setFocusedFire(null);
    setViewMode('2d');
    updateCamera({
      pitch: -90,
      heading: 0,
    });
  }, [updateCamera]);

  const value = useMemo(
    () => ({
      viewMode,
      setViewMode: handleSetViewMode,
      camera,
      updateCamera,
      flyTo,
      pendingFlyTo,
      clearPendingFlyTo,
      terrainExaggeration,
      setTerrainExaggeration,
      is3DEnabled,
      setIs3DEnabled,
      focusedFire,
      setFocusedFire,
      enterFocused3DMode,
      exitFocused3DMode,
    }),
    // Only state values that actually change should be dependencies
    // Callbacks (handleSetViewMode, updateCamera, flyTo, etc.) are stable via useCallback
    [viewMode, camera, pendingFlyTo, terrainExaggeration, is3DEnabled, focusedFire]
  );

  return (
    <MapViewContext.Provider value={value}>{children}</MapViewContext.Provider>
  );
}

// Hook to use the map view context
export function useMapView(): MapViewContextType {
  const context = useContext(MapViewContext);
  if (!context) {
    throw new Error('useMapView must be used within a MapViewProvider');
  }
  return context;
}

// Utility functions for coordinate conversion

/**
 * Convert Leaflet zoom level to camera altitude (approximate)
 */
export function zoomToAltitude(zoom: number): number {
  // Rough conversion: zoom 0 = 40,000km, zoom 20 = ~40m
  // Formula: altitude = 40000000 / 2^zoom
  return 40000000 / Math.pow(2, zoom);
}

/**
 * Convert camera altitude to Leaflet zoom level (approximate)
 */
export function altitudeToZoom(altitude: number): number {
  // Inverse of above formula
  // zoom = log2(40000000 / altitude)
  return Math.log2(40000000 / Math.max(altitude, 1));
}

/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
